import React from 'react';
import { Users, Briefcase, Building2, ArrowRight, CheckCircle } from 'lucide-react';

/**
 * Engagement Models Component - Inspired by Smartsocs
 * - Visual cards showing different partnership options
 * - Clear value propositions for each model
 */

const models = [
    {
        icon: Users,
        title: "Staff Augmentation",
        subtitle: "Extend Your Team",
        color: "#55C3BD",
        description: "Scale your engineering capacity with pre-vetted VLSI experts who integrate seamlessly with your team.",
        features: [
            "Dedicated engineers on-site or remote",
            "Interview & select your team",
            "Full project lifecycle support",
            "Flexible scaling up or down"
        ]
    },
    {
        icon: Briefcase,
        title: "Project-Based Delivery",
        subtitle: "Turnkey Solutions",
        color: "#EE7623",
        description: "Complete ownership of defined deliverables with fixed scope, timeline, and budget.",
        features: [
            "End-to-end project ownership",
            "Milestone-based delivery",
            "Dedicated project management",
            "Risk mitigation frameworks"
        ]
    },
    {
        icon: Building2,
        title: "Offshore Development Center",
        subtitle: "Your Extended Lab",
        color: "#0079C2",
        description: "Build a dedicated, captive engineering center that operates as an extension of your organization.",
        features: [
            "Fully managed facilities",
            "Custom hiring & training",
            "IP security & compliance",
            "Long-term partnership model"
        ]
    }
];

const EngagementModels: React.FC = () => {
    return (
        <section id="engagement" className="py-24 bg-[#153860] relative overflow-hidden">
            {/* Background */}
            <div
                className="absolute inset-0 opacity-30"
                style={{
                    backgroundImage: `radial-gradient(circle at 30% 70%, rgba(85, 195, 189, 0.2) 0%, transparent 50%)`
                }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#55C3BD]/10 border border-[#55C3BD]/30 text-[#55C3BD] text-xs font-semibold uppercase tracking-wider mb-6">
                        <Briefcase size={14} /> Flexible Partnerships
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                        Engagement <span className="text-[#55C3BD]">Models</span>
                    </h2>
                    <p className="text-lg text-white/60 max-w-3xl mx-auto">
                        Choose the partnership structure that best fits your project requirements and business objectives
                    </p>
                </div>

                {/* Models Grid */}
                <div className="grid md:grid-cols-3 gap-8">
                    {models.map((model, idx) => {
                        const Icon = model.icon;
                        return (
                            <div
                                key={idx}
                                className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#55C3BD]/50 transition-all duration-300 overflow-hidden"
                            >
                                {/* Accent Line */}
                                <div
                                    className="absolute top-0 left-0 right-0 h-1 transition-all duration-300 group-hover:h-2"
                                    style={{ backgroundColor: model.color }}
                                />

                                {/* Icon */}
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                                    style={{ background: `linear-gradient(135deg, ${model.color}40, ${model.color}20)` }}
                                >
                                    <Icon size={32} style={{ color: model.color }} />
                                </div>

                                {/* Content */}
                                <div className="text-xs text-white/50 uppercase tracking-wider mb-2">{model.subtitle}</div>
                                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#55C3BD] transition-colors">
                                    {model.title}
                                </h3>
                                <p className="text-white/60 mb-6 leading-relaxed">
                                    {model.description}
                                </p>

                                {/* Features */}
                                <ul className="space-y-3 mb-8">
                                    {model.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-3 text-white/70 text-sm">
                                            <CheckCircle size={16} className="text-[#55C3BD] mt-0.5 shrink-0" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA */}
                                <a
                                    href="#contact"
                                    className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                                    style={{ color: model.color }}
                                >
                                    Learn More
                                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </a>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default EngagementModels;
